import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// Components
import OrdersList from "../orders/table/OrdersList";
import TransactionListItem from "./transactions/TransactionListItem";
import MoneyTextField from "../Inputs/MoneyTextField";

// MUI
import {
  alpha,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Icons
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaymentIcon from "@mui/icons-material/Payment";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Redux
import { updatePaymentMethodValue } from "../../store/actions/cashierSlice";

// Utils
import { formatFinance } from "../../utils/formatters";
import { dateTimeMask } from "../../common/dateMask";

// --- Animation variants ---
const panelVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: "easeOut" },
  },
};

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04 },
  },
};

// --- Collapsible sidebar section ---
const SidebarSection = ({
  icon: Icon,
  title,
  count,
  color,
  defaultOpen = true,
  badge,
  children,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          px: 2,
          py: 1.5,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          userSelect: "none",
          transition: "background 0.15s ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.action.hover, 0.04),
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon sx={{ color, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          {count > 0 && (
            <Chip
              label={count}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                minWidth: 24,
                height: 22,
                bgcolor: alpha(color, 0.12),
                color: color,
              }}
            />
          )}
          {badge}
        </Stack>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        )}
      </Box>
      <Collapse in={open}>
        <Divider />
        {children}
      </Collapse>
    </Paper>
  );
};

// --- Empty state ---
const EmptyState = ({ icon: Icon, message }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        textAlign: "center",
      }}
    >
      <Icon
        sx={{ fontSize: 28, color: theme.palette.grey[300], mb: 1 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
        {message}
      </Typography>
    </Box>
  );
};

export default function ClosingCashierView({
  // Data
  reportOrders,
  setReportOrders,
  cashierTransactions,
  cashierPaymentMethods,
  showButtonMore,
  // Handlers
  loadReportUnfinishedOrders,
  handleShowDialog,
  handlePaymentTypeTransferDialog,
  handleIsClosingCashier,
  handleCloseCashier,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Payment methods completion
  const filledCount = cashierPaymentMethods.filter(
    (m) => m.value !== null && m.value !== "" && m.value !== undefined
  ).length;
  const totalCount = cashierPaymentMethods.length;
  const progress = totalCount > 0 ? (filledCount / totalCount) * 100 : 0;
  const allFilled = filledCount === totalCount && totalCount > 0;

  return (
    <Box sx={{ px: 3, pb: 3 }}>
      {/* ==================== STATUS HEADER BAR ==================== */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.04),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          {/* Left: Status + Progress */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.warning.main, 0.12),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PointOfSaleIcon
                sx={{ color: theme.palette.warning.dark, fontSize: 22 }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  Fechamento em andamento
                </Typography>
                {allFilled && (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 18, color: theme.palette.success.main }}
                  />
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 0.5 }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.divider, 0.15),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      bgcolor: allFilled
                        ? theme.palette.success.main
                        : theme.palette.primary.main,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {filledCount}/{totalCount} pagamentos
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {/* Right: Action buttons */}
          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CancelIcon />}
                onClick={handleIsClosingCashier}
                size={isMobile ? "small" : "medium"}
                sx={{
                  fontWeight: 600,
                  borderColor: alpha(theme.palette.text.secondary, 0.3),
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.04),
                  },
                }}
              >
                {!isMobile && "Cancelar"}
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<PointOfSaleIcon />}
                onClick={handleCloseCashier}
                size={isMobile ? "small" : "medium"}
                sx={{
                  fontWeight: 600,
                  px: 3,
                  boxShadow: allFilled
                    ? `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                    : "none",
                }}
              >
                {isMobile ? "Concluir" : "Concluir Fechamento"}
              </Button>
            </motion.div>
          </Stack>
        </Stack>
      </Paper>

      {/* ==================== 2-PANEL LAYOUT ==================== */}
      {/* On mobile: payments first (task), then context below */}
      <Grid
        container
        spacing={2.5}
        direction={isTablet ? "column-reverse" : "row"}
      >
        {/* ===== LEFT: Reference Sidebar (Orders + Transactions) ===== */}
        <Grid item xs={12} md={5}>
          <motion.div
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.08 }}
          >
            <Stack spacing={2}>
              {/* Open Orders */}
              <SidebarSection
                icon={ReceiptLongIcon}
                title="Pedidos em Aberto"
                count={reportOrders.length}
                color={theme.palette.warning.main}
                defaultOpen={reportOrders.length > 0}
                badge={
                  reportOrders.length > 0 ? (
                    <WarningAmberIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.warning.main,
                        ml: 0.5,
                      }}
                    />
                  ) : null
                }
              >
                <Box sx={{ p: 1.5, maxHeight: 300, overflow: "auto" }}>
                  {reportOrders.length === 0 ? (
                    <EmptyState
                      icon={CheckCircleOutlineIcon}
                      message="Nenhum pedido em aberto"
                    />
                  ) : (
                    <>
                      <OrdersList
                        header={null}
                        loadOrders={loadReportUnfinishedOrders}
                        showButtonMore={showButtonMore}
                        orders={reportOrders}
                        setOrders={setReportOrders}
                        type="cashier"
                      />
                      <Box
                        sx={{
                          mt: 1.5,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.warning.main, 0.06),
                          border: `1px solid ${alpha(
                            theme.palette.warning.main,
                            0.12
                          )}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                        >
                          <WarningAmberIcon
                            sx={{
                              fontSize: 15,
                              color: theme.palette.warning.main,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Resolva os pedidos pendentes antes de fechar
                          </Typography>
                        </Stack>
                      </Box>
                    </>
                  )}
                </Box>
              </SidebarSection>

              {/* Transactions */}
              <SidebarSection
                icon={EventNoteIcon}
                title="Movimentações"
                count={cashierTransactions.length}
                color={theme.palette.primary.main}
                defaultOpen={false}
              >
                <Box
                  sx={{
                    maxHeight: 320,
                    overflow: "auto",
                    px: 0.5,
                    py: cashierTransactions.length === 0 ? 0 : 0.5,
                  }}
                >
                  {cashierTransactions.length === 0 ? (
                    <EmptyState
                      icon={ReceiptLongIcon}
                      message="Nenhuma movimentação neste caixa"
                    />
                  ) : (
                    <AnimatePresence>
                      <motion.div
                        variants={listVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <List sx={{ p: 0 }}>
                          {cashierTransactions.map((transaction) => (
                            <TransactionListItem
                              key={transaction.id}
                              transaction={transaction}
                              formatFinance={formatFinance}
                              dateTimeMask={dateTimeMask}
                              remove={() =>
                                handleShowDialog(
                                  transaction.id,
                                  "deletePendentPayment"
                                )
                              }
                              setAsResolved={() =>
                                handleShowDialog(
                                  transaction.id,
                                  "resolvePendentPayment"
                                )
                              }
                            />
                          ))}
                        </List>
                      </motion.div>
                    </AnimatePresence>
                  )}
                </Box>
              </SidebarSection>
            </Stack>
          </motion.div>
        </Grid>

        {/* ===== RIGHT: Payment Methods (PRIMARY TASK) ===== */}
        <Grid item xs={12} md={7}>
          <motion.div
            variants={panelVariants}
            initial="hidden"
            animate="visible"
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(
                  allFilled
                    ? theme.palette.success.main
                    : theme.palette.primary.main,
                  0.15
                )}`,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
                borderTop: `3px solid ${
                  allFilled
                    ? theme.palette.success.main
                    : theme.palette.primary.main
                }`,
              }}
            >
              {/* Header */}
              <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PaymentIcon
                      sx={{
                        color: allFilled
                          ? theme.palette.success.main
                          : theme.palette.primary.main,
                        fontSize: 24,
                      }}
                    />
                    <Typography variant="h6" fontWeight={700}>
                      Métodos de Pagamento
                    </Typography>
                  </Stack>
                  <Chip
                    icon={
                      allFilled ? (
                        <CheckCircleIcon sx={{ fontSize: "16px !important" }} />
                      ) : undefined
                    }
                    label={`${filledCount}/${totalCount}`}
                    size="small"
                    color={allFilled ? "success" : "default"}
                    variant={allFilled ? "filled" : "outlined"}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, pl: 4.25 }}
                >
                  Informe o valor recebido em cada método de pagamento
                </Typography>
              </Box>
              <Divider />

              {/* Payment method list */}
              <Box sx={{ p: 3, maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
                <Stack spacing={2}>
                  {cashierPaymentMethods.map((method) => {
                    const isFilled =
                      method.value !== null &&
                      method.value !== "" &&
                      method.value !== undefined;

                    return (
                      <Box key={method.payment_method}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(
                              isFilled
                                ? theme.palette.success.main
                                : theme.palette.divider,
                              isFilled ? 0.25 : 0.15
                            )}`,
                            bgcolor: isFilled
                              ? alpha(theme.palette.success.main, 0.02)
                              : alpha(theme.palette.background.default, 0.3),
                            transition: "all 0.2s ease",
                          }}
                        >
                          {/* Method name + status indicator */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mb: 1.5 }}
                          >
                            {isFilled ? (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 18,
                                  color: theme.palette.success.main,
                                }}
                              />
                            ) : (
                              <RadioButtonUncheckedIcon
                                sx={{
                                  fontSize: 18,
                                  color: theme.palette.grey[300],
                                }}
                              />
                            )}
                            <Typography variant="subtitle2" fontWeight={600}>
                              {method.payment_method}
                            </Typography>
                          </Stack>

                          {/* Input + Transfer button */}
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{ flex: 1 }}>
                              <MoneyTextField
                                id={`payment-method-${method.payment_method}`}
                                label="Valor recebido"
                                value={method.value || ""}
                                setValue={(val) =>
                                  dispatch(
                                    updatePaymentMethodValue({
                                      payment_method: method.payment_method,
                                      value: val,
                                    })
                                  )
                                }
                                error={method.error || false}
                                helperText={method.helperText || ""}
                                required
                                fullWidth
                              />
                            </Box>
                            <Tooltip title="Transferir entre métodos">
                              <IconButton
                                color="primary"
                                onClick={() =>
                                  handlePaymentTypeTransferDialog(method)
                                }
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.08
                                  ),
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.16
                                    ),
                                  },
                                }}
                              >
                                <CurrencyExchangeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          {/* Transfers */}
                          {method.transfers.length > 0 && (
                            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                              {method.transfers.map((transfer, tIdx) => (
                                <Box
                                  key={`transfer-${tIdx}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    py: 0.75,
                                    px: 1.5,
                                    borderRadius: 1,
                                    bgcolor: alpha(
                                      theme.palette.background.default,
                                      0.8
                                    ),
                                    border: `1px solid ${alpha(
                                      theme.palette.divider,
                                      0.12
                                    )}`,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {transfer.from_payment_method}
                                      <Box
                                        component="span"
                                        sx={{ mx: 0.5, opacity: 0.5 }}
                                      >
                                        →
                                      </Box>
                                      {transfer.to_payment_method}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      color="primary"
                                    >
                                      {formatFinance(
                                        transfer.amount_cents / 100
                                      )}
                                    </Typography>
                                  </Box>
                                  <Tooltip title="Remover">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleShowDialog(
                                          transfer.id,
                                          "paymentMethodTransfer"
                                        )
                                      }
                                      sx={{
                                        ml: 1,
                                        p: 0.5,
                                        color: theme.palette.error.main,
                                        opacity: 0.6,
                                        "&:hover": { opacity: 1 },
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Paper>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
}
